import type { StateCreator } from 'zustand';
import type { Course, Term, AbsenceEntry, BudgetAdjustment, ParticipationEntry } from '@abs/shared-types';
import { generateId } from '@/utils/id';

/** Minimal shape needed from other slices for cross-slice operations */
interface AbsenceSliceDependency {
  entries: AbsenceEntry[];
  budgetAdjustments: BudgetAdjustment[];
  participationEntries: ParticipationEntry[];
}

export interface CourseSlice {
  courses: Course[];
  selectedCourseId: string | null;
  terms: Term[];

  addCourse: (course: Omit<Course, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateCourse: (id: string, updates: Partial<Omit<Course, 'id' | 'createdAt'>>) => void;
  deleteCourse: (id: string) => void;
  selectCourse: (id: string | null) => void;
  addTerm: (term: Omit<Term, 'id'>) => string;
  updateTerm: (id: string, updates: Partial<Omit<Term, 'id'>>) => void;
  archiveTerm: (id: string) => void;
}

export const createCourseSlice: StateCreator<CourseSlice & AbsenceSliceDependency, [], [], CourseSlice> = (set) => ({
  courses: [],
  selectedCourseId: null,
  terms: [],

  addCourse: (courseData) => {
    const id = generateId();
    const now = new Date().toISOString();
    const course: Course = {
      ...courseData,
      id,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ courses: [...state.courses, course] }));
    return id;
  },

  updateCourse: (id, updates) => {
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c,
      ),
    }));
  },

  deleteCourse: (id) => {
    set((state) => ({
      courses: state.courses.filter((c) => c.id !== id),
      entries: state.entries.filter((e) => e.courseId !== id),
      budgetAdjustments: state.budgetAdjustments.filter((a) => a.courseId !== id),
      participationEntries: state.participationEntries.filter((p) => p.courseId !== id),
      selectedCourseId: state.selectedCourseId === id ? null : state.selectedCourseId,
    }));
  },

  selectCourse: (id) => {
    set({ selectedCourseId: id });
  },

  addTerm: (termData) => {
    const id = generateId();
    const term: Term = { ...termData, id };
    set((state) => ({ terms: [...state.terms, term] }));
    return id;
  },

  updateTerm: (id, updates) => {
    set((state) => ({
      terms: state.terms.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    }));
  },

  archiveTerm: (id) => {
    set((state) => ({
      terms: state.terms.map((t) =>
        t.id === id ? { ...t, isArchived: true } : t,
      ),
    }));
  },
});
